import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Receipt, TrendingUp } from "lucide-react";
import { format } from "date-fns";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function Sales() {
  const { user } = useAuth();
  const [sales, setSales] = useState(null);

  useEffect(() => {
    api.get("/resellers/sales").then(({ data }) => setSales(data)).catch(() => toast.error("Failed to load sales"));
  }, []);

  const total = (sales || []).reduce((s, r) => s + (r.amount || 0), 0);

  return (
    <div data-testid="sales-page">
      <PageHeader overline="Ledger" title="Sales History" description="Every subscription sale and renewal.">
        <Badge variant="outline" className="border-success/40 text-success gap-1.5 text-sm px-3 py-1.5">
          <TrendingUp className="h-4 w-4" /> {Number(total).toLocaleString()} credits total
        </Badge>
      </PageHeader>

      <Card className="border-border overflow-hidden">
        {!sales ? (
          <div className="p-4 space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
        ) : sales.length === 0 ? (
          <div className="p-12 text-center">
            <Receipt className="h-10 w-10 mx-auto text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">No sales recorded yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Type</TableHead>
                  {user.role === "super_admin" && <TableHead className="hidden sm:table-cell">Seller</TableHead>}
                  <TableHead className="text-right">Credits</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sales.map((s) => (
                  <TableRow key={s.id} data-testid={`sale-row-${s.id}`}>
                    <TableCell className="text-xs text-muted-foreground font-mono">{s.created_at ? format(new Date(s.created_at), "dd MMM yyyy HH:mm") : "—"}</TableCell>
                    <TableCell className="font-mono text-sm">{s.customer_username}</TableCell>
                    <TableCell><Badge variant="secondary">{s.plan_name}</Badge></TableCell>
                    <TableCell><Badge variant="outline" className={s.kind === "renewal" ? "border-accent/40 text-accent" : "border-primary/40 text-primary"}>{s.kind}</Badge></TableCell>
                    {user.role === "super_admin" && <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{s.seller_name}</TableCell>}
                    <TableCell className="text-right font-mono font-medium text-success">{Number(s.amount).toLocaleString()} cr</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
